import { UploadFile } from 'antd/lib/upload/interface';
import { isEmpty } from 'lodash';
import { getFileNameWithoutExtension, getValidUploadedFiles, isValidStayLength } from '../modules/crm/inPatient/service/crmUtils';
import {
  actions,
  BUDDHIMED_NAV_EDIT,
  BUDDHIMED_NAV_EDIT_HIS,
  BUDDHIMED_NAV_EDIT_NON_HIS,
  BUDDHIMED_NAV_FREE_EDIT,
  BUDDHIMED_NAV_FREE_EDIT_HIS,
  BUDDHIMED_NAV_FREE_EDIT_NON_HIS,
  BUDDHIMED_NAV_IR,
  BUDDHIMED_NAV_NEW_ADMISSION,
  BUDDHIMED_NAV_NEW_ADMISSION_HIS,
  BUDDHIMED_NAV_NEW_ADMISSION_NON_HIS,
  BUDDHIMED_NAV_SETTLEMENT,
  CHARS_TO_REPLACE,
  EXTENDED_STATUS,
  HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW,
  NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW,
  NOT_ALLOWED_CHARACTERS,
  NOT_ALLOWED_CHARACTERS_RPA,
  RECONCILIATION_FILE_TYPES,
  UPLOAD_STATUS,
} from '../shared/constants/crmConstants';
import * as moment from 'moment';
import ApplicationDetail from '../shared/services/applicationDetail.service';
import { centeredNotification } from './utility';
import { isEqual, startOfMinute } from 'date-fns';
import { toTitleCase } from './customPipeUtil';

export const fileBeforeUpload = ({ type, name, size }: UploadFile, selectedPayer: any, maxFileSize?: any, nextStatus?: any): boolean => {
  const ALLOWED_FILE_EXTENSIONS = ApplicationDetail.instance.getAllowedFileExtension();
  const allowedFileTypeObj = selectedPayer?.allowedFileTypes;
  const allowedFileType = JSON.parse(allowedFileTypeObj || '{}')?.[nextStatus] || null;
  const ALLOWED_FILE_TYPES = allowedFileType?.split(',');
  const maxFileNameCharsObj = selectedPayer?.maxFileNameCharacters;
  const maxFileNameChars = parseInt(JSON.parse(maxFileNameCharsObj || '{}')?.[nextStatus], 10);
  const fileNameWithoutExtension = Boolean(maxFileNameChars) ? getFileNameWithoutExtension(name) : null;
  const fileExtension = name?.split('.').pop();
  if (size === 0) {
    centeredNotification(`${name} size should not be 0 byte`);
    return false;
  }
  const MB2Bytes = 1024 * 1024;
  const maxFileLimit = maxFileSize * MB2Bytes;
  if (size && size > maxFileLimit) {
    centeredNotification(`Maximum file limit is ${maxFileLimit / MB2Bytes}MB.`);
    return false;
  }
  if (fileNameWithoutExtension && fileNameWithoutExtension?.length > maxFileNameChars) {
    centeredNotification(`Selected file name is too long. Please rename the file max upto ${maxFileNameChars} characters`);
    return false;
  }
  if (type && ALLOWED_FILE_EXTENSIONS.TYPES.indexOf(type) < 0 && allowedFileType === null) {
    centeredNotification(
      `Invalid file format. Please upload only ${ALLOWED_FILE_EXTENSIONS.LABEL} file.`,
    );
    return false;
  }
  if (allowedFileType !== null && type && !ALLOWED_FILE_TYPES?.includes('.' + fileExtension)) {
    centeredNotification(
      `Invalid file format. Please upload only ${allowedFileType} file.`,
    );
    return false;
  }
  const FINAL_NOT_ALLOWED_CHARACTERS = selectedPayer?.isRpaPayer ? NOT_ALLOWED_CHARACTERS_RPA : NOT_ALLOWED_CHARACTERS;
  if (FINAL_NOT_ALLOWED_CHARACTERS.test(getFileNameWithoutExtension(name))) {
    const message = extractFileUploadErrorMessage(FINAL_NOT_ALLOWED_CHARACTERS, name);
    centeredNotification(message);
    return false;
  }
  return !!type && ALLOWED_FILE_EXTENSIONS.TYPES.indexOf(type) >= 0;
};

const extractFileUploadErrorMessage = (restrictedCharaters: RegExp, name: string): string => {
  const NOT_ALLOWED_CHARACTERS_ = restrictedCharaters.source;
  // Extract the restricted special characters
  const specialChars = NOT_ALLOWED_CHARACTERS_.replace(CHARS_TO_REPLACE, '').split('').join(' ');
  const message = `The document name ${name} should not contain special characters ${specialChars}
     or characters outside the ASCII range (such as á, é, ó, ü, ç, etc.)`;
  return message;
};

export const hisFileBeforeUpload = (
  { type, name, size }: UploadFile,
  selectedPayer: any,
  maxFileSize?: any,
  nextStatus?: any,
  updateErrorMessage: ((msg: string) => void) = (): any => { }, // default to empty function
  setIsValidFile: ((valid: boolean) => void) = (): any => { }, // Function to update validity state
  isValidFile: boolean = true,
): boolean => {
  const ALLOWED_FILE_EXTENSIONS = ApplicationDetail.instance.getAllowedFileExtension();
  const allowedFileTypeObj = selectedPayer?.allowedFileTypes;
  const allowedFileType = JSON.parse(allowedFileTypeObj || '{}')?.[nextStatus] || null;
  const ALLOWED_FILE_TYPES = allowedFileType?.split(',');
  const maxFileNameCharsObj = selectedPayer?.maxFileNameCharacters;
  const maxFileNameChars = parseInt(JSON.parse(maxFileNameCharsObj || '{}')?.[nextStatus], 10);
  const fileNameWithoutExtension = Boolean(maxFileNameChars) ? getFileNameWithoutExtension(name) : null;
  const fileExtension = name?.split('.').pop();
  setIsValidFile(isValidFile);
  if (size === 0) {
    updateErrorMessage(`${name} size should not be 0 byte`);
    setIsValidFile(false);
    return false;
  }
  const MB2Bytes = 1024 * 1024;
  const maxFileLimit = maxFileSize * MB2Bytes;
  if (size && size > maxFileLimit) {
    updateErrorMessage(`Maximum file limit is ${maxFileLimit / MB2Bytes}MB.`);
    setIsValidFile(false);
    return false;
  }

  if (fileNameWithoutExtension && fileNameWithoutExtension?.length > maxFileNameChars) {
    updateErrorMessage(`Selected file name is too long. Please rename the file max up to ${maxFileNameChars} characters`);
    setIsValidFile(false);
    return false;
  }
  if (type && ALLOWED_FILE_EXTENSIONS.TYPES.indexOf(type) < 0 && allowedFileType === null) {
    updateErrorMessage(`Invalid file format. Please upload only ${ALLOWED_FILE_EXTENSIONS.LABEL} file.`);
    setIsValidFile(false);
    return false;
  }
  if (allowedFileType !== null && type && !ALLOWED_FILE_TYPES?.includes('.' + fileExtension)) {
    updateErrorMessage(`Invalid file format. Please upload only ${allowedFileType} file.`);
    setIsValidFile(false);
    return false;
  }
  const FINAL_NOT_ALLOWED_CHARACTERS = selectedPayer?.isRpaPayer ? NOT_ALLOWED_CHARACTERS_RPA : NOT_ALLOWED_CHARACTERS;
  if (FINAL_NOT_ALLOWED_CHARACTERS.test(getFileNameWithoutExtension(name))) {
    const message = extractFileUploadErrorMessage(FINAL_NOT_ALLOWED_CHARACTERS, name);
    updateErrorMessage(message);
    setIsValidFile(false);
    return false;
  }
  if (isValidFile) {
    updateErrorMessage('');
  }
  return !!type && ALLOWED_FILE_EXTENSIONS.TYPES.indexOf(type) >= 0;
};

export const fileUploadBeforeCheck = ({ type, name, size }: UploadFile): boolean => {
  if (size === 0) {
    centeredNotification(`${name} size should not be 0 byte`);
    return false;
  }
  if (size && size > 20971520) {
    centeredNotification(`Maximum file limit is 20MB.`);
    return false;
  }
  if (type && RECONCILIATION_FILE_TYPES.indexOf(type) < 0) {
    centeredNotification(
      `Invalid file format. Please upload only ${RECONCILIATION_FILE_TYPES.join(', ')} file.`,
    );
  }
  if (NOT_ALLOWED_CHARACTERS.test(name)) {
    const message = extractFileUploadErrorMessage(NOT_ALLOWED_CHARACTERS, name);

    centeredNotification(message);
    return false;
  }
  return !!type && RECONCILIATION_FILE_TYPES.indexOf(type) >= 0;
};

export const getModalNav = ({ minimizeCount, isEditPage, actionName, isIRAvailable, isFreeEditAvailable,
  isEditDischargePage, isHisSearchEnabled, isPreAuthNewDesignEnabled, isHISClaim, claimDetails, selectedPatientDetail,
  hospitalSearchPatientDetails, isEditReconsider, documentFlow, triggerCancellationModal }: any): any[] => {
  if (minimizeCount > 0) {
    return documentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW ? BUDDHIMED_NAV_NEW_ADMISSION_HIS :
      documentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW ? BUDDHIMED_NAV_NEW_ADMISSION_NON_HIS : BUDDHIMED_NAV_NEW_ADMISSION;
  } else if (isEditPage || isEditDischargePage || isEditReconsider) {
    if (isIRAvailable) {
      return BUDDHIMED_NAV_IR;
    }
    if (actionName === actions.EDIT && isFreeEditAvailable) {
      return (documentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW) ? BUDDHIMED_NAV_FREE_EDIT_HIS :
        documentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW ? BUDDHIMED_NAV_FREE_EDIT_NON_HIS : BUDDHIMED_NAV_FREE_EDIT;
    }
    if (actionName === actions.CASHLESSSUMBIT) {
      return BUDDHIMED_NAV_SETTLEMENT;
    } else {
      return (documentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW) ? BUDDHIMED_NAV_EDIT_HIS :
        documentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW ? BUDDHIMED_NAV_EDIT_NON_HIS : BUDDHIMED_NAV_EDIT;
    }
  } else if (actionName === actions.CANCEL) {
    triggerCancellationModal();
    return [];
  } else {
    return (documentFlow === HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW) ?
      BUDDHIMED_NAV_NEW_ADMISSION_HIS : documentFlow === NON_HIS_NEW_DESIGN_DOCUMENT_UPLOAD_FLOW
        ? BUDDHIMED_NAV_NEW_ADMISSION_NON_HIS : BUDDHIMED_NAV_NEW_ADMISSION;
  }
};

export const hospitalizationModalValidation = ({ form, updateAdmissionForm, goNext, isDateApplied,
  hospitalSearchPatientDetails, setErrorMessage }: any): void => {
  const patientTreatmentDetails = hospitalSearchPatientDetails?.[0]?.treatmentDetails;
  form?.validateFields().then((): void => {
    const { dateOfAdmission, dateOfDischarge, stayLength } =
      form?.getFieldsValue(['dateOfAdmission', 'dateOfDischarge', 'stayLength']);
    const isHISDates = patientTreatmentDetails?.dateOfAdmission
      ? isEqual(startOfMinute(new Date(patientTreatmentDetails?.dateOfAdmission)), startOfMinute(new Date(stayLength[0])))
      : false;
    if ((dateOfAdmission && dateOfDischarge) || (isValidStayLength(stayLength) && (isHISDates ? true : isDateApplied))) {
      updateAdmissionForm({ dateOfAdmission, dateOfDischarge, stayLength });
      goNext();
    }
  });
};

export const hospitalizationModalValidationForSettlement = ({ form, updateAdmissionForm, goNext, setErrorMessage }: any): void => {
  form?.validateFields().then((): void => {
    const todayDate = moment.default().startOf('day');
    const { dateOfAdmission, dateOfDischarge } = form?.getFieldsValue(['dateOfAdmission', 'dateOfDischarge']);
    if (dateOfDischarge?.isBefore(todayDate)) {
      updateAdmissionForm({ dateOfAdmission, dateOfDischarge });
      goNext();
    } else {
      setErrorMessage('Data shows that the patient is still in the hospital, update DOD for initiating settlement or click on "Edit" to enhance a claim request');
    }
  });
};

export const fileUploadValidation = ({
  uploadedFiles,
  goNext,
  setErrorMessage,
}: any): void => {
  const validFiles: any[] = getValidUploadedFiles(uploadedFiles);
  if (!isEmpty(validFiles)) {
    goNext();
  } else {
    setErrorMessage('Please upload at-least 1 file.');
  }
};

export const getTitle = (uploadedFiles: any): any => {
  return uploadedFiles?.length > 0 ? 'Documents Uploaded' : 'Add Documents';
};

export const hisGetTitle = (uploadedFiles: any): any => {
  return uploadedFiles?.length > 0 ? 'Documents Uploaded' : 'Upload Documents';
};

export const nonHisGetTitle = (uploadedFiles: any): any => {
  return uploadedFiles?.length > 0 ? 'Documents Uploaded' : 'Scan and Upload Documents';
};

export const lastTabFileUploadValidation = ({
  uploadedFiles,
  goNext,
  setErrorMessage,
  onMinimize,
  form,
  minFileCount,
}: any): void => {
  const validFiles: any[] = getValidUploadedFiles(uploadedFiles);
  form?.validateFields().then((): any => {
    if (!isEmpty(validFiles) && validFiles?.length >= minFileCount) {
      goNext();
      onMinimize(validFiles);
    } else {
      setErrorMessage(`Please upload at-least ${minFileCount} file.`);
    }
  });
};

export const getNextForNewAdmission = ({ selectedTab }: any): any => {
  if (selectedTab === 2) {
    return 'Submit';
  }
  return 'Next';
};

export const lastTabSubmitIr = ({
  uploadedFiles,
  setErrorMessage,
  onSubmitIr,
  minFileCount,
  form,
}: any): void => {
  const validFiles: any[] = getValidUploadedFiles(uploadedFiles);
  form?.validateFields().then((): any => {
    if (!isEmpty(validFiles) && validFiles?.length >= minFileCount) {
      onSubmitIr();
    } else {
      setErrorMessage(`Please upload at-least ${minFileCount} file.`);
    }
  });
};

export const lastTabSubmitSettlement = ({
  uploadedFiles,
  setErrorMessage,
  onSubmitSettlement,
  onSubmitSettlementCourier,
  form,
  settlementType,
  minFileCount,
  settlementValue,
  exactFileCount,
  maxFileCount,
}: any): void => {
  form?.validateFields().then((): void => {
    const validFiles: any[] = getValidUploadedFiles(uploadedFiles);
    if (form.getFieldValue('selectedOldFile')) {
      validFiles.push(...form.getFieldValue('selectedOldFile'));
    }
    if (settlementType === 'settlement-docs' || settlementValue === 'settlement-docs') {
      if (exactFileCount && !isEmpty(validFiles) && validFiles?.length !== exactFileCount) {
        setErrorMessage(`Please upload ${exactFileCount} file(s).`);
      } else if (minFileCount && !isEmpty(validFiles) && validFiles?.length < minFileCount) {
        setErrorMessage(`Please upload at-least ${minFileCount} file(s).`);
      } else if (maxFileCount && !isEmpty(validFiles) && validFiles?.length > maxFileCount) {
        setErrorMessage(`Please upload at-most ${maxFileCount} file(s).`);
      } else {
        onSubmitSettlement();
      }
    } else if (settlementType === 'courier-track' || settlementValue === 'courier-track') {
      onSubmitSettlementCourier();
    } else {
      centeredNotification('Please select any of the two options to continue submission');
    }
  });
};

export const getFileStatus = (uploadStatus: string, extendedStatus: string, existingRecord: any): string => {
  if (uploadStatus === UPLOAD_STATUS.done && (extendedStatus === EXTENDED_STATUS.verified || existingRecord)) {
    return 'Verified';
  } else if (uploadStatus === UPLOAD_STATUS.done && extendedStatus === EXTENDED_STATUS.verifying) {
    return 'Verifying...';
  } else if (uploadStatus === UPLOAD_STATUS.done) {
    return 'Uploaded';
  } else if (uploadStatus === UPLOAD_STATUS.uploading) {
    return 'Uploading...';
  } else {
    return '';
  }
};

export const getFileType = (fileType: any[] | undefined): string => {
  if (!Array.isArray(fileType) || fileType?.length === 0) {
    return '';
  }

  const formattedFileTypes = fileType
    ?.filter((type: any): any => type.toUpperCase() !== 'NHI')
    ?.map((fType: any): any => toTitleCase(fType?.replace(/_/g, ' ')));

  return formattedFileTypes?.length ? formattedFileTypes?.join(', ') : '';
};

// Function to get MIME type from file extension
export const getMimeTypeFromExtension = (fileName: string): string => {
  const fileExtension = fileName?.split('.').pop()?.toLowerCase();

  switch (fileExtension) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'bmp':
      return 'image/bmp';
    case 'webp':
      return 'image/webp';
    case 'tiff':
    case 'tif':
      return 'image/tiff';
    case 'svg':
      return 'image/svg+xml';
    case 'pdf':
      return 'application/pdf';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'txt':
      return 'text/plain';
    default:
      return 'application/pdf'; // default fallback
  }
};
